import { ReactNode } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow, format } from "date-fns";
import { Link } from "react-router-dom";
import { Enquiry } from "./model";

function fmtDate(d?: string) {
  if (!d) return null;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return null;
  return format(dt, "d MMM yyyy");
}

function tripDates(e: Enquiry): string {
  const inD = fmtDate(e.checkIn);
  const outD = fmtDate(e.checkOut);
  if (inD && outD) return `${inD} – ${outD}`;
  if (inD) return inD;
  if (e.flexibleDaysRange) return `Flexible · ${e.flexibleDaysRange}`;
  return "—";
}

const chip = (cls: string, label: ReactNode) => (
  <span className={`inline-flex items-center gap-1 py-1 px-2.5 rounded-full text-sm font-medium whitespace-nowrap ${cls}`}>
    {label}
  </span>
);

export const enquiriesColumns: ColumnDef<Enquiry>[] = [
  {
    header: "Client",
    cell: ({ row }) => {
      const c = row.original.customer;
      const name = c?.fullName || [c?.firstName, c?.lastName].filter(Boolean).join(" ") || c?.email || "—";
      return (
        <div className="flex flex-col">
          <span className="font-semibold whitespace-nowrap">{name}</span>
          {c?.email && <span className="text-xs text-slate-500 whitespace-nowrap">{c.email}</span>}
        </div>
      );
    },
  },
  {
    header: "Destination",
    cell: ({ row }) => {
      const e = row.original;
      const region = e.charterRegion && e.charterRegion !== e.country ? e.charterRegion : null;
      if (!e.country && !region) return <span className="text-slate-300">—</span>;
      return (
        <span className="text-slate-700 whitespace-nowrap">
          {e.country || region}
          {e.country && region ? <span className="text-slate-400"> · {region}</span> : null}
        </span>
      );
    },
  },
  {
    header: "Dates",
    cell: ({ row }) => <span className="text-slate-600 whitespace-nowrap">{tripDates(row.original)}</span>,
  },
  {
    header: "Party",
    cell: ({ row }) => {
      const e = row.original;
      if (!e.guests) return <span className="text-slate-300">—</span>;
      return (
        <span className="text-slate-600 whitespace-nowrap">
          {e.guests} guest{e.guests === 1 ? "" : "s"}
        </span>
      );
    },
  },
  {
    header: "Budget",
    cell: ({ row }) => {
      const e = row.original;
      if (!e.budget) return <span className="text-slate-300">—</span>;
      const code = e.currencyCode || "GBP";
      return (
        <span className="font-medium whitespace-nowrap">
          {new Intl.NumberFormat("en-GB", { style: "currency", currency: code, maximumFractionDigits: 0 }).format(
            e.budget,
          )}
        </span>
      );
    },
  },
  {
    header: "Shortlist",
    cell: ({ row }) => {
      const n = row.original.interestedInOtherEnquiries?.length ?? 0;
      if (!n) return <span className="text-slate-300">—</span>;
      return chip("bg-blue-100 text-blue-700", `${n} yacht${n === 1 ? "" : "s"}`);
    },
  },
  {
    header: "Deal",
    cell: ({ row }) => {
      const e = row.original;
      const hasDeal = !!e.hubspotId && e.hubspotId !== "NO_HUBSPOT_ID";
      if (!hasDeal) return <span className="text-slate-400 whitespace-nowrap">Not created</span>;
      const badge = chip("bg-emerald-100 text-emerald-700", "Deal created");
      return e.dealLink ? (
        <a
          href={e.dealLink}
          target="_blank"
          rel="noreferrer"
          onClick={(ev) => ev.stopPropagation()}
          className="hover:opacity-80"
          title="Open the deal in HubSpot"
        >
          {badge}
        </a>
      ) : (
        badge
      );
    },
  },
  {
    header: "Offers",
    cell: ({ row }) => {
      const offers = row.original.offers ?? [];
      if (!offers.length) return <span className="text-slate-400 whitespace-nowrap">Not started</span>;
      const badge = chip("bg-green-100 text-green-700", `${offers.length} offer${offers.length === 1 ? "" : "s"}`);
      // Link to the (first) offer's page — from there the broker reaches the booking.
      const offerId = offers[0]?.id;
      return offerId ? (
        <Link to={`/offers/${offerId}`} onClick={(ev) => ev.stopPropagation()} className="hover:opacity-80">
          {badge}
        </Link>
      ) : (
        badge
      );
    },
  },
  {
    header: "Received",
    cell: ({ row }) => {
      const d = row.original.dateCreated;
      if (!d) return <span className="text-slate-300">—</span>;
      return (
        <span className="text-slate-500 whitespace-nowrap">
          {formatDistanceToNow(new Date(d), { addSuffix: true })}
        </span>
      );
    },
  },
];
